import { ThumbsUp, MessageCircle, Share2, Globe } from "lucide-react";

interface MetaAdPreviewProps {
  headline: string | null;
  body: string | null;
  callToAction: string | null;
  imageUrl: string | null;
  landingPageUrl: string | null;
  creativeType: string;
  accountName?: string;
  aspectRatio?: "square" | "landscape" | "portrait" | "auto";
  /** Real page/brand logo URL — falls back to the initial-letter avatar. */
  logoUrl?: string | null;
}

export function MetaAdPreview({ headline, body, callToAction, imageUrl, landingPageUrl, creativeType, accountName, aspectRatio = "auto", logoUrl }: MetaAdPreviewProps) {
  const domain = landingPageUrl ? (() => { try { return new URL(landingPageUrl).hostname.replace("www.", ""); } catch { return "example.com"; } })() : "example.com";

  const computedRatio = aspectRatio === "auto"
    ? (creativeType === "carousel" ? "landscape" : "square")
    : aspectRatio;

  const imageAspect = computedRatio === "landscape" ? "aspect-[1.91/1]" : computedRatio === "portrait" ? "aspect-[4/5]" : "aspect-square";

  return (
    <div className="w-[400px] bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="meta-ad-preview">
      <div className="px-3 py-2.5 flex items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img src={logoUrl} alt={accountName || "Page"} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold text-sm">{(accountName || "A").charAt(0)}</span>
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">{accountName || "Advertiser"}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            Sponsored · <Globe className="w-3 h-3" />
          </p>
        </div>
      </div>

      {body && (
        <div className="px-3 pb-2">
          <p className="text-sm text-gray-900 leading-snug">{body}</p>
        </div>
      )}

      <div className={`relative w-full ${imageAspect} bg-gray-100`}>
        {imageUrl ? (
          <img src={imageUrl} alt="Ad creative" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 rounded-lg bg-gray-200 mx-auto mb-2 flex items-center justify-center">
                <span className="text-2xl text-gray-400">{creativeType === "video" ? "▶" : "🖼"}</span>
              </div>
              <p className="text-xs text-gray-400">{creativeType === "video" ? "Video Creative" : creativeType === "carousel" ? "Carousel Creative" : "Image Creative"}</p>
              <p className="text-[10px] text-gray-300 mt-1">{computedRatio === "landscape" ? "1.91:1 landscape" : "1:1 square"}</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-gray-500 uppercase truncate">{domain}</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{headline || "Ad Headline"}</p>
          </div>
          {callToAction && (
            <button className="ml-3 px-4 py-1.5 bg-[#E4E6EB] text-sm font-semibold text-gray-900 rounded-md whitespace-nowrap" data-testid="meta-cta-button">
              {callToAction.replace(/_/g, " ")}
            </button>
          )}
        </div>
      </div>

      <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-around">
        <button className="flex items-center gap-1.5 text-gray-500 text-sm hover:bg-gray-50 px-3 py-1 rounded">
          <ThumbsUp className="w-4 h-4" /> Like
        </button>
        <button className="flex items-center gap-1.5 text-gray-500 text-sm hover:bg-gray-50 px-3 py-1 rounded">
          <MessageCircle className="w-4 h-4" /> Comment
        </button>
        <button className="flex items-center gap-1.5 text-gray-500 text-sm hover:bg-gray-50 px-3 py-1 rounded">
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>
    </div>
  );
}
